// ✅ src/pages/Sales/EMIPaymentReport_Print.jsx  (FULL FILE)
// Calls: GET /api/emiListing/getById?enquiryNo=
// Open like: /Sales/EMIPaymentReport_Print?enquiryNo=22
//
// Backend mount:
//   app.use("/api/emiListing", emiPaymentListing);

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

// helper to read query params
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// safe number parser (removes commas)
function toNumber(v) {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/,/g, "").trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

// format number as 60,000.00 (no ₹ symbol to match your print)
function fmt2(v) {
  const num = toNumber(v);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// date to DD/MM/YYYY if backend returns Date string
function fmtDateDDMMYYYY(v) {
  if (!v) return "";
  // if already like 25/03/2021 return as-is
  if (typeof v === "string" && v.includes("/")) return v;
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const EMIPaymentReportPrint = () => {
  const q = useQuery();

  // ✅ IMPORTANT: We use enquiryNo (because tbl_sales_emi has fld_enquiryno)
  const enquiryNo = q.get("enquiryNo") || "";

  const [customerData, setCustomerData] = useState({
    customerId: "",
    customerName: "",
    productName: "",
    salesPerson: "",
    totalSqFeet: "",
    overDueIntRate: "0.00",
    amountMonth: "0.00",
    plotNoAssetCode: "",
    location: "",
    dateOfBooking: "",
    assetValue: "0.00",
    paymentMode: "",
    interestRate: "0.00",
    tenurePeriod: "",
  });

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ call getById API
  useEffect(() => {
    const fetchPrintData = async () => {
      if (!enquiryNo) {
        setError("enquiryNo missing in url. Use ?enquiryNo=123");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`${BASE_URL}/api/emiListing/getById`, {
          params: { enquiryNo },
        });
console.log("res:", res.data);
        if (!res.data?.success) {
          throw new Error(res.data?.message || "Failed to load record");
        }

        const d = res.data.data || {};
        const history = res.data.history || res.data.paymentHistory || [];

        // Header mapping (backend returns minimal; we keep defaults if missing)
        setCustomerData({
          customerId: d.customerId ?? "",
          customerName: d.customerName ?? "",
          productName: d.productName ?? d.assetTypeModel ?? "",
          salesPerson: d.salesPerson ?? "",
          totalSqFeet: d.totalSqFeet ?? d.totalSqft ?? "",
          overDueIntRate: d.overDueIntRate ?? d.overdueInterest ?? "0.00",
          amountMonth: d.amountMonth ?? d.emiAmountMonth ?? "0.00",
          plotNoAssetCode: d.plotNoAssetCode ?? d.assetCode ?? "",
          location: d.location ?? "HEAD-OFFICE",
          dateOfBooking: fmtDateDDMMYYYY(d.dateOfBooking ?? d.bookingDate ?? ""),
          assetValue: d.assetValue ?? "0.00",
          paymentMode: d.paymentMode ?? "",
          interestRate: d.interestRate ?? "0.00",
          tenurePeriod: d.tenurePeriod ?? "",
        });

        // History mapping (table)
        setPaymentHistory(
          history.map((x, i) => ({
            sNo: x.sNo ?? i + 1,
            date: fmtDateDDMMYYYY(x.date || x.fld_emidate || x.paymentDate || ""),
            billNo: x.billNo || x.fld_billno || x.fld_emid || "",
            phase: x.phase || x.fld_remarks || x.fld_phase || "",
            payableAmount: x.payableAmount ?? x.fld_loanamount ?? x.fld_payableamount ?? 0,
            paidAmount: x.paidAmount ?? x.fld_dueamount ?? x.fld_paidamount ?? 0,
            balance: x.balance ?? x.fld_balanceamount ?? x.fld_balance ?? 0,
            overDue: x.overDue ?? x.fld_inerestamount ?? x.fld_overdueamount ?? 0,
            status: x.status || x.fld_status || x.paystatus || "",
          }))
        );

        // ✅ optional auto print (keep small delay so DOM renders)
        setTimeout(() => window.print(), 400);
      } catch (e) {
        setError(e?.message || "Error loading record");
      } finally {
        setLoading(false);
      }
    };

    fetchPrintData();
  }, [enquiryNo]);

  // Total paid amount (sum of paidAmount)
  const totalPaidAmount = paymentHistory.reduce((sum, item) => sum + toNumber(item.paidAmount), 0);

  if (loading) return <div className="p-6">Loading Print...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white font-sans text-sm print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          {/* Logo Section */}
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>

          {/* Company Info Section */}
          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641030, Tamil Nadu, India</div>
                <div>Phone No:9003744644 Email: infraconsoft-implementation@nissiinfotech.com</div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center py-2 border-b border-black font-bold">EMI Payment Report</div>
      </div>

      {/* Customer Details Section */}
      <div className="border-l-2 border-r-2 border-black">
        <div className="grid grid-cols-2 text-sm">
          {/* Left Column */}
          <div className="border-r border-black">
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Customer ID</div>
              <div className="px-2">: {customerData.customerId}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Customer Name</div>
              <div className="px-2">: {customerData.customerName}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Product Name</div>
              <div className="px-2">: {customerData.productName}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Sales Person</div>
              <div className="px-2">: {customerData.salesPerson}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Total Sq.Feet</div>
              <div className="px-2">: {customerData.totalSqFeet}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">OverDue Int Rate</div>
              <div className="px-2">: {customerData.overDueIntRate}</div>
            </div>

            <div className="flex py-1">
              <div className="w-40 px-2 font-medium">Amount / Month</div>
              <div className="px-2">: {fmt2(customerData.amountMonth)}</div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Plot No / Asset Code</div>
              <div className="px-2">: {customerData.plotNoAssetCode}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Location</div>
              <div className="px-2">: {customerData.location}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Date of Booking</div>
              <div className="px-2">: {customerData.dateOfBooking}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Asset Value</div>
              <div className="px-2">: {fmt2(customerData.assetValue)}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Payment Mode</div>
              <div className="px-2">: {customerData.paymentMode}</div>
            </div>

            <div className="flex border-b border-gray-300 py-1">
              <div className="w-40 px-2 font-medium">Interest Rate</div>
              <div className="px-2">: {customerData.interestRate}</div>
            </div>

            <div className="flex py-1">
              <div className="w-40 px-2 font-medium">Tenure Period</div>
              <div className="px-2">: {customerData.tenurePeriod}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History Table */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-2 py-2 w-10 text-center">S.No</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Date</th>
              <th className="border-r border-black px-2 py-2 w-16 text-center">Bill No</th>
              <th className="border-r border-black px-2 py-2 w-40 text-center">Phase</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Payable Amount</th>
              <th className="border-r border-black px-2 py-2 w-28 text-center">Paid Amount / Due Amount</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Balance Amount</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Over Due + Int Amount</th>
              <th className="px-2 py-2 w-20 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {paymentHistory.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-2 py-2 text-center">{item.sNo}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.date}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.billNo}</td>
                <td className="border-r border-black px-2 py-2">{item.phase}</td>
                <td className="border-r border-black px-2 py-2 text-right">{fmt2(item.payableAmount)}</td>
                <td className="border-r border-black px-2 py-2 text-right">{fmt2(item.paidAmount)}</td>
                <td className="border-r border-black px-2 py-2 text-right">{fmt2(item.balance)}</td>
                <td className="border-r border-black px-2 py-2 text-right">{fmt2(item.overDue)}</td>
                <td className="px-2 py-2 text-center">{item.status}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black font-bold bg-gray-50">
              <td colSpan="5" className="border-r border-black px-2 py-2 text-center">
                Total
              </td>
              <td className="border-r border-black px-2 py-2 text-right">{fmt2(totalPaidAmount)}</td>
              <td colSpan="3" className="px-2 py-2"></td>
            </tr>

            {paymentHistory.length === 0 && (
              <tr>
                <td colSpan="9" className="py-6 text-center">
                  No payment history found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Date */}
      <div className="mt-8 text-xs">{new Date().toLocaleDateString("en-GB")}</div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          table {
            border-collapse: collapse !important;
          }
          th,
          td {
            border: 1px solid black !important;
          }
          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .bg-gray-50 {
            background-color: #f9fafb !important;
          }
          .bg-black {
            background-color: black !important;
            color: white !important;
          }
          @page {
            margin: 0.5in;
            size: A4 portrait;
          }
        }
      `}</style>
    </div>
  );
};

export default EMIPaymentReportPrint;
